import { useState } from "react";
import { toast } from "sonner";
import api, { fileUrl, uploadFile } from "@/lib/api";
import { FileText, File as FileIcon, ExternalLink, CheckCircle2, AlertTriangle, MessageSquare, Trash2, Loader2, Upload, X } from "lucide-react";

const STATUS = {
  validated: { label: "Validé", cls: "text-emerald-700 bg-emerald-100", Icon: CheckCircle2 },
  to_revise: { label: "À revoir", cls: "text-amber-700 bg-amber-100", Icon: AlertTriangle },
};

function FileLink({ file }) {
  const ct = file.content_type || "";
  const Icon = ct.includes("pdf") ? FileText : FileIcon;
  return (
    <a href={fileUrl(file.id)} target="_blank" rel="noreferrer"
      className="flex items-center gap-2 rounded-xl border border-slate-200 p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
      <Icon className="h-5 w-5 text-slate-500 shrink-0" />
      <span className="text-xs font-bold text-slate-700 truncate flex-1">{file.name}</span>
      <ExternalLink className="h-4 w-4 text-slate-400 shrink-0" />
    </a>
  );
}

function ReviewControls({ doc, onChanged }) {
  const [status, setStatus] = useState(doc.validation_status === "pending" ? "" : doc.validation_status);
  const [comment, setComment] = useState(doc.teacher_comment || "");
  const [busy, setBusy] = useState(false);
  const [correctionFile, setCorrectionFile] = useState(null);
  const [correctionFileRemoved, setCorrectionFileRemoved] = useState(false);

  const existingCorrectionFile = !correctionFileRemoved && !correctionFile ? doc.teacher_comment_file : null;

  const clearCorrectionFile = () => {
    setCorrectionFile(null);
    setCorrectionFileRemoved(true);
  };

  const save = async () => {
    if (!status) { toast.error("Choisis un statut (Validé ou À revoir)."); return; }
    setBusy(true);
    try {
      let comment_file_id = doc.teacher_comment_file?.id || null;
      if (correctionFile) {
        const up = await uploadFile(correctionFile);
        comment_file_id = up.id;
      } else if (correctionFileRemoved) {
        comment_file_id = null;
      }
      await api.patch(`/admin/career-docs/${doc.id}/review`, { status, comment, comment_file_id });
      toast.success("Évaluation enregistrée ✓");
      onChanged && onChanged();
    } catch {
      toast.error("Enregistrement impossible");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 space-y-2.5 rounded-xl bg-slate-50 border border-slate-100 p-3">
      <div className="flex gap-2">
        <button type="button" onClick={() => setStatus("validated")} data-testid={`career-validate-${doc.id}`}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${status === "validated" ? "bg-emerald-500 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Validé
        </button>
        <button type="button" onClick={() => setStatus("to_revise")} data-testid={`career-revise-${doc.id}`}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${status === "to_revise" ? "bg-amber-500 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-300"}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> À revoir
        </button>
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Commentaire pour l'élève (facultatif)…"
        rows={2}
        data-testid={`career-comment-${doc.id}`}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
      />
      <div>
        {existingCorrectionFile && (
          <div className="flex items-center gap-2 mb-1.5 rounded-xl bg-white border border-slate-200 p-2" data-testid={`career-file-existing-${doc.id}`}>
            <FileText className="h-4 w-4 text-orange-500 shrink-0" />
            <a href={fileUrl(existingCorrectionFile.id)} target="_blank" rel="noreferrer" className="text-xs font-bold text-slate-700 truncate flex-1 hover:underline">{existingCorrectionFile.name}</a>
            <button type="button" onClick={clearCorrectionFile} data-testid={`career-file-remove-${doc.id}`} className="h-6 w-6 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><X className="h-3 w-3" /></button>
          </div>
        )}
        {correctionFile && (
          <div className="flex items-center gap-2 mb-1.5 rounded-xl bg-white border border-slate-200 p-2" data-testid={`career-file-preview-${doc.id}`}>
            <FileText className="h-4 w-4 text-orange-500 shrink-0" />
            <span className="text-xs font-bold text-slate-700 truncate flex-1">{correctionFile.name}</span>
            <button type="button" onClick={clearCorrectionFile} data-testid={`career-file-discard-${doc.id}`} className="h-6 w-6 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center shrink-0"><X className="h-3 w-3" /></button>
          </div>
        )}
        <label data-testid={`career-file-input-${doc.id}`} className="inline-flex items-center gap-1.5 rounded-full border-2 border-slate-200 hover:border-orange-300 text-slate-600 hover:text-orange-600 px-3 py-1.5 text-xs font-bold transition-colors cursor-pointer">
          <Upload className="h-3.5 w-3.5" /> {existingCorrectionFile || correctionFile ? "Remplacer le document" : "Joindre un document corrigé (facultatif)"}
          <input type="file" className="hidden" onChange={(e) => { setCorrectionFile(e.target.files[0] || null); setCorrectionFileRemoved(false); }} />
        </label>
      </div>
      <button onClick={save} disabled={busy} data-testid={`career-save-${doc.id}`}
        className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white rounded-full px-4 py-2 font-bold text-xs transition-colors">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Enregistrer
      </button>
    </div>
  );
}

export default function CareerDocCard({ doc, onChanged, reviewMode = false, onDelete }) {
  const st = STATUS[doc.validation_status];
  return (
    <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-3.5" data-testid={`career-doc-${doc.id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        {st ? (
          <span data-testid={`career-doc-status-${doc.id}`} className={`inline-flex items-center gap-1 text-[11px] font-extrabold px-2 py-0.5 rounded-full ${st.cls}`}>
            <st.Icon className="h-3 w-3" /> {st.label}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-extrabold px-2 py-0.5 rounded-full text-slate-500 bg-slate-100">En attente</span>
        )}
        <span className="text-[11px] font-bold text-slate-400">{(doc.created_at || "").slice(0, 10)}</span>
        {onDelete && (
          <button onClick={() => onDelete(doc.id)} data-testid={`career-doc-delete-${doc.id}`}
            className="ml-auto h-7 w-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {doc.file && <FileLink file={doc.file} />}
      {(doc.teacher_comment || doc.teacher_comment_file) && (
        <div className="mt-2.5 rounded-xl bg-orange-50 border border-orange-100 p-2.5" data-testid={`career-doc-comment-${doc.id}`}>
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-orange-700 mb-1 flex items-center gap-1.5"><MessageSquare className="h-3 w-3" /> Commentaire du professeur</p>
          {doc.teacher_comment && <p className="text-sm text-slate-700">{doc.teacher_comment}</p>}
          {doc.teacher_comment_file && (
            <a href={fileUrl(doc.teacher_comment_file.id)} target="_blank" rel="noreferrer" data-testid={`career-doc-comment-file-${doc.id}`}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-orange-700 hover:underline mt-1.5">
              <FileText className="h-3.5 w-3.5" /> {doc.teacher_comment_file.name} (document corrigé) <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
      {reviewMode && <ReviewControls doc={doc} onChanged={onChanged} />}
    </div>
  );
}
